import Big from 'big.js';
import { NumberConvertOptions, RadixPointType, ToRoundProps } from '../types/common';
import { OrderSide, OrderUnitType } from '../types/trade';
import { VaildNumber } from './format';
import { getPrecision } from './helper';

Big.NE = -19;

function toRound({ int, decimal, precision, intLen = 0, decimalLen = 0 }: ToRoundProps) {
  const safeDecimalLen = 8,
    safeIntLen = 15;
  intLen = intLen || int.length;
  decimalLen = decimalLen || decimal.length;
  let i = 1,
    dCarryFlag = false,
    returnDecimal = '';
  while (i && i <= Math.ceil(decimalLen / safeDecimalLen)) {
    const newDecimal = decimal.slice(-i * safeDecimalLen, (1 - i) * safeDecimalLen || precision + 1);
    const newDecimalLen = newDecimal.length;
    let status: 'INCREASE' | 'DAP' = 'INCREASE';

    if (i === 1) {
      const temp = Number(`0.${newDecimal}`).toFixed(Math.min(precision, safeDecimalLen - 1));
      if (temp.startsWith('1')) {
        returnDecimal = '0'.repeat(newDecimalLen - 1);
      } else {
        returnDecimal = decimal.slice(0, -safeDecimalLen) + temp.slice(2);
        status = 'DAP';
      }
    } else {
      const temp = (Number(newDecimal) + 1).toString();
      if (temp.length > newDecimalLen) {
        returnDecimal = '0'.repeat(newDecimalLen) + returnDecimal;
      } else {
        returnDecimal = decimal.slice(0, -i * safeDecimalLen) + temp + returnDecimal;
        status = 'DAP';
      }
    }

    if (status === 'INCREASE') {
      i++;
      dCarryFlag = true;
    } else {
      i = 0;
      dCarryFlag = false;
    }
  }
  i = 1; // reset

  let iCarryFlag = false,
    returnInt = '';
  if (dCarryFlag) {
    while (i && i <= Math.ceil(intLen / safeIntLen)) {
      const newInt = int.slice(-i * safeIntLen, (1 - i) * safeIntLen || intLen);
      const newIntLen = newInt.length;
      const temp = (Number(newInt) + 1).toString();
      if (temp.length > newIntLen) {
        i++;
        iCarryFlag = true;
        returnInt = '0'.repeat(newIntLen) + returnInt;
      } else {
        returnInt = int.slice(0, -i * safeIntLen) + temp + returnInt;
        i = 0;
        iCarryFlag = false;
      }
    }
  }

  return [iCarryFlag ? `1${'0'.repeat(int.length)}` : returnInt || int, returnDecimal];
}

function toPrecision(str: string, precision: number, type: RadixPointType, originStringLen?: number) {
  if (typeof str !== 'string' || precision <= 0) return '';
  let len = originStringLen ?? str.length;

  switch (type) {
    case 'NORMAL':
      return len === 0 ? '' : '.' + str.slice(0, precision);
    case 'SUPPLY_ZERO':
      return '.' + (len < precision ? str.padEnd(precision, '0') : str.slice(0, precision));
    default: {
      let i = 1;
      len = Math.min(len, precision);
      str = str.slice(0, len);
      while (i && i <= Math.ceil(len / 16)) {
        const temp = Number(`0.${str.slice(-16)}`);
        if (temp === 0) {
          str = str.slice(0, -16);
          i++;
        } else {
          str = str.slice(0, -16) + toNonExponential(temp).slice(2);
          i = 0;
        }
      }
      return str ? `.${str}` : '';
    }
  }
}

export function toNonExponential(num: number): string {
  const m = num.toExponential().match(/\d(?:\.(\d*))?e([+-]\d+)/);
  return num.toFixed(Math.max(0, (m?.[1] || '').length - Number(m?.[2] || 0)));
}

export function toFraction(str = '', place = 3, originStringLen?: number): string {
  const reg = new RegExp(`(\\d)(?=(?:\\d{${place}})+$)`, 'g');
  return (originStringLen ?? str.length) > place ? str.replace(reg, '$1,') : str;
}

export function toConvertNumber(str: string, options?: NumberConvertOptions): string {
  const { precision = 8, separator = true, round = false } = options || {};
  let radixPoint = options?.radixPoint || 'NORMAL';

  if (typeof str !== 'string' || !str.length || Number.isNaN(Number(str))) return '';
  let [int = '0', decimal = ''] = str.split('.');
  let sign = int.slice(0, 1); // for + -
  ['+', '-'].includes(sign) ? (int = int.slice(1)) : (sign = '');

  // if (precision < 0) {
  //   const n = int.padStart(Math.abs(precision) + 1, '0');
  //   return int === '0'
  //     ? int
  //     : toConvertNumber(`${n.slice(0, precision)}.${n.slice(precision)}`, {
  //         precision: Math.abs(precision),
  //         separator,
  //       });
  // }
  if (precision < 0) {
    const l = int.length - Math.abs(precision);
    return int === '0' ? int : `${sign === '-' ? sign : ''}${int.slice(0, l)}${'0'.repeat(Math.abs(precision))}`;
  }
  const decimalLen = decimal.length;
  int = Number(int) === 0 ? '0' : int;
  const intLen = int.length;

  if (round && precision < decimalLen) {
    decimal = decimal.slice(0, precision + 1);
    [int, decimal] = toRound({ int, decimal, precision, intLen, decimalLen: decimal.length });
    radixPoint = 'NORMAL';
  }

  const newInt = separator ? toFraction(int, undefined, intLen) : int;
  const newDecimal = toPrecision(
    decimal,
    precision,
    round && precision >= decimalLen ? 'SUPPLY_ZERO' : radixPoint,
    decimalLen
  );

  const r = newInt + newDecimal;
  return Number(r) === 0 || sign !== '-' ? r : sign + r;
}

export const calculateQuantity = (
  price: string,
  amount: string,
  contract: string,
  unit: OrderUnitType,
  multiplier: string
): string => {
  if (amount === '' || amount === '0' || Number(amount) === 0) {
    return '0';
  }

  const quantityFormula = {
    '0': () => {
      const value = new Big(VaildNumber(amount)).div(VaildNumber(contract, 1)).toString();
      return toConvertNumber(value, { precision: getPrecision(multiplier), separator: false });
    },
    '1': () => {
      return amount;
    },
    '2': () => {
      if (VaildNumber(price) <= 0) {
        return '0';
      } else {
        if (
          new Big(multiplier)
            .minus(new Big(VaildNumber(amount)).div(VaildNumber(price, 1)).div(VaildNumber(contract, 1)))
            .abs()
            .lt(new Big(0.2).times(multiplier))
        ) {
          return multiplier;
        } else {
          const count = new Big(VaildNumber(amount))
            .div(VaildNumber(price, 1))
            .div(VaildNumber(contract, 1))
            .toString();
          return toConvertNumber(count, { precision: getPrecision(multiplier), separator: false });
        }
      }
    },
  };
  return quantityFormula[unit]();
};

export const calculatePercentageQuantity = (
  maxBuy: string,
  maxSell: string,
  percentage: number,
  side: OrderSide,
  multiplier: number
): string => {
  const quantityFormula = {
    BUY: () => {
      try {
        return toConvertNumber(new Big(VaildNumber(maxBuy)).times(VaildNumber(percentage)).div(100).toString(), {
          precision: multiplier,
          separator: false,
        });
      } catch (error) {
        console.log(error);
      }
      return '0';
    },
    SELL: () => {
      try {
        return toConvertNumber(new Big(VaildNumber(maxSell)).times(VaildNumber(percentage)).div(100).toString(), {
          precision: multiplier,
          separator: false,
        });
      } catch (error) {
        console.log(error);
      }
      return '0';
    },
  };
  return side === 'BUY' || side === 'buy' ? quantityFormula.BUY() : quantityFormula.SELL();
};
